/**
 * Confluence-owned renderer for the Invited App Shortcuts GRS recommendations
 * query.
 *
 * Confluence does NOT use the AFM entry-points contract (Jira's PUMA-2000 does).
 * Instead it follows the same SSR pattern as `preloadPlatformNavigationQuery`:
 *
 *   1. `preloadInvitedAppShortcutsRecommendationsQuery` `fetchQuery`s this
 *      operation at route resolution time (SSR), populating the Relay store.
 *   2. This renderer reads the SAME operation + variables with
 *      `useLazyLoadQuery({ fetchPolicy: 'store-or-network' })`, so the
 *      SSR-preloaded record is served synchronously with no client round-trip.
 *
 * The raw AGG response is handed verbatim to the shared pure translator
 * `buildInvitedAppShortcutsRecommendationsResult` so the discriminated result
 * type stays a single source of truth across Jira and Confluence. That translator
 * is also what keeps this query's selection set honest — it is the contract, so
 * the selection below must keep providing everything it reads.
 */
import React, { Suspense, useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { InvitedAppShortcutsContainer } from '@atlassian/cross-join-nudges/invited-app-shortcuts/invited-app-shortcuts-container/main';
import { useInvitedAppShortcuts } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-shortcuts';
import type {
	ShortcutsConfig,
	ShortcutsSlotProps,
} from '@atlassian/global-side-navigation/common/types';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { useRecommendedApps } from './hooks/useRecommendedApps.tsx';
import { buildInvitedAppShortcutsRecommendationsVariables } from './invitedAppShortcutsRecommendationsVariables.ts';
import type { InvitedAppShortcutsRecommendationsQuery } from './__generated__/InvitedAppShortcutsRecommendationsQuery.graphql.ts';

const SOURCE_APP = 'confluence' as const;

/**
 * The Relay operation. Kept as a module-level constant so both this renderer
 * and the drift test can reference the same document.
 */
export const InvitedAppShortcutsRecommendationsQueryNode: GraphQLTaggedNode = graphql`
	query InvitedAppShortcutsRecommendationsQuery($tenantId: ID, $product: String!) {
		growthRecommendations {
			recommendations(
				context: { tenantId: $tenantId, product: $product, useCase: "invitedAppShortcuts" }
			) {
				__typename
				... on GrowthRecRecommendations {
					data {
						entityId
						reasons
						score
					}
					context {
						tenantId
						product
						useCase
						sessionId
					}
				}
				... on QueryError {
					identifier
					message
					extensions {
						statusCode
						errorType
					}
				}
			}
		}
	}
`;

export type InvitedAppShortcutsRecommendationsProps = {
	/**
	 * The pre-bound GSN shortcuts menu handed to us by the `ShortcutsSlot`. We
	 * render it with the decorated `referrableShortcuts` items.
	 */
	Component: ShortcutsSlotProps['Component'];
	cloudId: string;
	/** Base shortcuts config reconstructed from the slot's resolved items. */
	shortcutsConfig: ShortcutsConfig;
	userId: string;
};

/**
 * Suspends on a cold Relay store (first paint before the SSR cache hydrates),
 * so it MUST be rendered inside a `<Suspense>` boundary by the caller.
 */
export function InvitedAppShortcutsRecommendations({
	Component,
	cloudId,
	shortcutsConfig,
	userId,
}: InvitedAppShortcutsRecommendationsProps): React.JSX.Element {
	// Memoised so referential equality is stable across re-renders — otherwise useLazyLoadQuery
	// treats it as a new request and refetches. Built from the shared builder so the values stay
	// identical to the preloader's (a mismatch would miss the SSR-seeded store record).
	const variables = useMemo(
		() => buildInvitedAppShortcutsRecommendationsVariables(cloudId),
		[cloudId],
	);

	const data = useLazyLoadQuery<InvitedAppShortcutsRecommendationsQuery>(
		InvitedAppShortcutsRecommendationsQueryNode,
		variables,
		// store-or-network: serve the SSR-preloaded record synchronously; do not
		// fire a second network request when the store is already populated.
		{ fetchPolicy: 'store-or-network' },
	);

	// Shared with the legacy interop surface. `data` is the generated `$data` type,
	// structurally assignable to the translator's response contract — no cast
	// needed. See `useRecommendedApps` for why this keys off the parsed `product`
	// rather than the raw `entityId`, and how `error` / `empty` fall through to
	// "no recommended apps" (matching control).
	const { recommendedApps, targetProducts } = useRecommendedApps(data, {
		evaluateLoomExperiment: true,
	});

	// Eligibility comes from `recommendedApps`, so no cookie cache is involved. Do NOT pass
	// `traits` / `onCacheNeedsRefresh` — those are experiment-mode inputs only.
	const resolverResult = useInvitedAppShortcuts({
		enabled: true,
		sourceProduct: SOURCE_APP,
		targetProducts,
		cloudId,
		userId,
		recommendedApps,
	});

	return (
		<InvitedAppShortcutsContainer
			cloudId={cloudId}
			userId={userId}
			recommendedApps={recommendedApps}
			sourceApp={SOURCE_APP}
			shortcutsConfig={shortcutsConfig}
			resolverResult={resolverResult}
		>
			{({ referrableShortcuts }) => (
				<Component shortcutItems={Object.values(referrableShortcuts)} />
			)}
		</InvitedAppShortcutsContainer>
	);
}

type FailOpenBoundaryProps = {
	children: React.ReactNode;
	/** Rendered if the recommendations subtree throws — the default shortcuts menu. */
	fallback: React.ReactNode;
};

/**
 * Fails the recommendations subtree open to the default
 * shortcuts menu. `useLazyLoadQuery` throws on a network/parse error above the
 * container's own error boundary, so we need a boundary here to keep the nav
 * rendering (control-cohort parity) rather than crashing.
 */
class FailOpenBoundary extends React.Component<FailOpenBoundaryProps, { hasError: boolean }> {
	static getDerivedStateFromError() {
		return { hasError: true };
	}

	constructor(props: FailOpenBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	componentDidCatch(error: Error) {
		// Report through the structured monitoring client (which scrubs/handles error payloads) rather
		// than logging the raw error, then mark it handled since we fail open to the default menu.
		getMonitoringClient().submitError(error, { attribution: Attribution.GROWTH_PUMA });
		markErrorAsHandled(error);
	}

	render() {
		return this.state.hasError ? this.props.fallback : this.props.children;
	}
}

/**
 * Slot-facing wrapper: scopes Suspense (cold Relay store) and the fail-open
 * ErrorBoundary to the shortcuts subtree. Both fall back to the pre-bound
 * `Component` (the default shortcuts menu) so a cold cache or a failed GraphQL
 * request never blocks the rest of the nav.
 */
export function SafeInvitedAppShortcutsRecommendations(
	props: InvitedAppShortcutsRecommendationsProps,
): React.JSX.Element {
	const { Component } = props;
	const fallback = <Component />;
	return (
		<Suspense fallback={fallback}>
			<FailOpenBoundary fallback={fallback}>
				<InvitedAppShortcutsRecommendations {...props} />
			</FailOpenBoundary>
		</Suspense>
	);
}
